import type { ContactTransactionsPage } from '../types/contacts';
import type {
  ReviewTrackingPatch,
  ContactListParams,
  ContactListResponse,
  ContactMergePayload,
  ContactRecord,
  ContactTransactionRecord,
  ContactUpsertPayload,
} from '../types/contacts';

// Re-export types for convenience
export type { ContactRecord, ContactUpsertPayload };
import { authFetch } from '../lib/authFetch';
import { isAbortError } from '../lib/apiErrorHandling';
import { buildQueryString } from '../lib/query';
import { resolveBase, json } from './client';
import { dedupeRequest } from '../lib/requestDeduplication';
import type { ApiErrorPayload, PaginationMeta } from './contracts';
import { StatementSendUnconfirmedError } from './statementSendError';

type ContactApiData = Partial<ContactRecord> & {
  items?: ContactRecord[];
  duplicates?: ContactDuplicateMatch[];
  transactions?: ContactTransactionRecord[];
  contact?: ContactRecord;
  fields?: Record<string, unknown>;
  qb_customer_id?: string;
  source?: string;
  display_name?: string | null;
};

type ContactApiResponse = ApiErrorPayload & {
  data?: ContactApiData;
  pagination?: PaginationMeta;
};

function normalizeContactRecord(contact: unknown): ContactRecord {
  const source = contact && typeof contact === 'object' ? contact as Partial<ContactRecord> : {};
  const billingCcEmails = Array.isArray(source.billing_cc_emails)
    ? source.billing_cc_emails
        .filter((email: unknown): email is string => typeof email === 'string')
        .map((email) => email.trim())
        .filter(Boolean)
        .filter(
          (email, index, emails) =>
            emails.findIndex((candidate) => candidate.toLowerCase() === email.toLowerCase()) === index,
        )
    : [];
  const tags: string[] = Array.isArray(source.tags)
    ? source.tags
        .filter((tag: unknown): tag is string => typeof tag === 'string')
        .map((tag: string) => tag.trim())
        .filter((tag: string) => tag.length > 0)
    : [];

  const dedupedTags: string[] = [];
  const seen = new Set<string>();
  tags.forEach((tag) => {
    const lower = tag.toLowerCase();
    if (seen.has(lower)) {
      return;
    }
    seen.add(lower);
    dedupedTags.push(tag);
  });

  return {
    ...source,
    billing_cc_emails: billingCcEmails,
    tags: dedupedTags,
  } as ContactRecord;
}

export async function listContacts(
  apiBase: string,
  params: ContactListParams = {}
): Promise<ContactListResponse> {
  const base = resolveBase(apiBase);
  const query = buildQueryString(
    {
      page: params.page ? params.page : undefined,
      page_size: params.page_size ? params.page_size : undefined,
      search: params.search ? params.search : undefined,
      tags: params.tags && params.tags.length > 0 ? params.tags : undefined,
      archived: params.archived === undefined ? undefined : params.archived,
      review_status: params.review_status,
      review_follow_up_due: params.review_follow_up_due || undefined,
    },
    { includeQuestionMark: true },
  );

  const url = `${base}/api/contacts${query}`;
  const fetchContacts = async () => {
    const response = await authFetch(url, { signal: params.signal });
    const data = await json<ContactApiResponse>(response);
    if (data.status === 'success') {
      const payload = data.data ?? {};
      return {
        items: (payload.items || []).map(normalizeContactRecord),
        pagination: data.pagination || {
          page: 1,
          page_size: 50,
          total: 0,
          total_pages: 1,
        },
      } as ContactListResponse;
    }
    return data as ContactListResponse;
  };

  if (params.signal) {
    return fetchContacts();
  }

  return dedupeRequest(url, fetchContacts);
}

export async function getContactById(apiBase: string, contactId: string): Promise<ContactRecord> {
  const base = resolveBase(apiBase);
  const url = `${base}/api/contacts/${contactId}`;
  const fetchContact = async () => {
    const response = await authFetch(url);
    const data = await json<ContactApiResponse>(response);
    if (data.status === 'success') {
      return normalizeContactRecord(data.data);
    }
    return data as ContactRecord;
  };

  return dedupeRequest(url, fetchContact);
}

export async function lookupContacts(
  apiBase: string,
  contactIds: string[],
  options: { signal?: AbortSignal } = {}
): Promise<ContactRecord[]> {
  if (!contactIds.length) {
    return [];
  }
  const base = resolveBase(apiBase);

  const MAX_IDS_PER_REQUEST = 200;
  const results: ContactRecord[] = [];

  for (let i = 0; i < contactIds.length; i += MAX_IDS_PER_REQUEST) {
    const chunk = contactIds.slice(i, i + MAX_IDS_PER_REQUEST);
    const response = await authFetch(`${base}/api/contacts/lookup`, {
      method: 'POST',
      signal: options.signal,
      body: JSON.stringify({ contact_ids: chunk }),
    });
    const data = await json<ContactApiResponse>(response);
    if (data.status === 'success') {
      const items = Array.isArray(data?.data?.items) ? data.data.items : [];
      results.push(...items.map(normalizeContactRecord));
      continue;
    }
    const message = typeof data?.message === 'string' ? data.message : 'Failed to lookup contacts';
    throw new Error(message);
  }

  return results;
}

export async function createContact(
  apiBase: string,
  payload: ContactUpsertPayload
): Promise<ContactRecord> {
  const base = resolveBase(apiBase);
  const response = await authFetch(`${base}/api/contacts`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
  const data = await json<ContactApiResponse>(response);
  if (data.status === 'success') {
    return normalizeContactRecord(data.data);
  }
  return data as ContactRecord;
}

export async function updateContact(
  apiBase: string,
  contactId: string,
  payload: Partial<ContactUpsertPayload>
): Promise<ContactRecord> {
  const base = resolveBase(apiBase);
  const response = await authFetch(`${base}/api/contacts/${contactId}`, {
    method: 'PUT',
    body: JSON.stringify(payload),
  });
  const data = await json<ContactApiResponse>(response);
  if (data.status === 'success') {
    return normalizeContactRecord(data.data);
  }
  return data as ContactRecord;
}

export async function mergeContacts(apiBase: string, payload: ContactMergePayload): Promise<ContactRecord> {
  const base = resolveBase(apiBase);
  const response = await authFetch(`${base}/api/contacts/merge`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
  const data = await json<ContactApiResponse>(response);
  if (data.status === 'success') {
    return normalizeContactRecord(data.data);
  }
  return data as ContactRecord;
}

export async function archiveContact(apiBase: string, contactId: string, reason?: string): Promise<ContactRecord> {
  const base = resolveBase(apiBase);
  const query = reason ? `?reason=${encodeURIComponent(reason)}` : '';
  const response = await authFetch(`${base}/api/contacts/${contactId}${query}`, {
    method: 'DELETE',
  });
  const data = await json<ContactApiResponse>(response);
  if (data.status === 'success') {
    const { status, ...contact } = data;
    return normalizeContactRecord(contact);
  }
  return data as ContactRecord;
}

export async function restoreContact(apiBase: string, contactId: string): Promise<ContactRecord> {
  const base = resolveBase(apiBase);
  const response = await authFetch(`${base}/api/contacts/${contactId}/restore`, {
    method: 'POST',
  });
  const data = await json<ContactApiResponse>(response);
  if (data.status === 'success') {
    const { status, ...contact } = data;
    return normalizeContactRecord(contact);
  }
  return data as ContactRecord;
}

export interface ContactDuplicateMatch {
  contact: ContactRecord;
  match_type: 'exact_email' | 'exact_phone' | 'similar_name';
  match_field: string;
  similarity?: number;
}

export interface CheckContactDuplicatesParams {
  first_name?: string;
  last_name?: string;
  email?: string;
  phone?: string;
}

export async function checkContactDuplicates(
  apiBase: string,
  params: CheckContactDuplicatesParams,
  options: { signal?: AbortSignal } = {}
): Promise<ContactDuplicateMatch[]> {
  const base = resolveBase(apiBase);
  const response = await authFetch(`${base}/api/contacts/check-duplicates`, {
    method: 'POST',
    signal: options.signal,
    body: JSON.stringify(params),
  });
  const data = await json<ContactApiResponse>(response);
  if (data.status === 'success') {
    return (data.data?.duplicates || []).map((match: ContactDuplicateMatch) => ({
      ...match,
      contact: normalizeContactRecord(match.contact),
    }));
  }
  return [];
}

export async function getContactTransactions(
  apiBase: string,
  contactId: string,
  params: { limit?: number; signal?: AbortSignal } = {}
): Promise<ContactTransactionRecord[]> {
  const base = resolveBase(apiBase);
  const query = buildQueryString(
    {
      limit: params.limit && params.limit > 0 ? Math.min(params.limit, 200) : undefined,
    },
    { includeQuestionMark: true },
  );

  const response = await authFetch(`${base}/api/contacts/${contactId}/quickbooks/transactions${query}`, {
    signal: params.signal,
  });
  const data = await json<ContactApiResponse>(response);
  if (data.status === 'success') {
    return Array.isArray(data.data?.transactions) ? data.data.transactions : [];
  }
  return [];
}

export async function getContactTransactionsPage(
  apiBase: string,
  contactId: string,
  params: { page?: number; page_size?: number; transaction_type?: 'Estimate' | 'Invoice' | 'Payment'; payment_state?: 'open' | 'paid'; signal?: AbortSignal } = {},
): Promise<ContactTransactionsPage> {
  const { signal, ...queryParams } = params;
  const query = buildQueryString({ page: 1, page_size: 5, ...queryParams }, { includeQuestionMark: true });
  const response = await authFetch(`${resolveBase(apiBase)}/api/contacts/${contactId}/quickbooks/transactions${query}`, { signal });
  const data = await json<{ status: string; data?: ContactTransactionsPage; message?: string }>(response);
  if (data.status !== 'success' || !data.data) throw new Error(data.message || 'Failed to load QuickBooks transactions');
  return data.data;
}

export interface CreateQuickBooksCustomerResult {
  qb_customer_id: string;
  contact: ContactRecord;
  source?: 'created' | 'linked_existing' | 'already_linked' | string;
  display_name?: string | null;
}

export async function createQuickBooksCustomer(
  apiBase: string,
  contactId: string,
): Promise<CreateQuickBooksCustomerResult> {
  const base = resolveBase(apiBase);
  const response = await authFetch(`${base}/api/contacts/${contactId}/quickbooks/customer`, {
    method: 'POST',
  });
  const data = await json<ContactApiResponse>(response);
  if (data.status === 'success') {
    const normalizedContact = data.data?.contact ? normalizeContactRecord(data.data.contact) : undefined;
    const contactPayload = normalizedContact || (data.data?.contact as ContactRecord | undefined);
    if (!contactPayload) {
      throw new Error('QuickBooks customer response was missing contact data');
    }
    return {
      qb_customer_id: data.data?.qb_customer_id || normalizedContact?.qb_customer_id || '',
      contact: contactPayload,
      source: data.data?.source,
      display_name: data.data?.display_name || normalizedContact?.name || null,
    };
  }
  const message = typeof data?.message === 'string' ? data.message : 'Failed to create QuickBooks customer';
  throw new Error(message);
}

export interface UpdateQuickBooksCustomerFieldsRequest {
  fields: string[];
}

export interface UpdateQuickBooksCustomerFieldsResult {
  qb_customer_id: string;
  fields: string[];
  display_name?: string | null;
}

export async function updateQuickBooksCustomerFields(
  apiBase: string,
  contactId: string,
  payload: UpdateQuickBooksCustomerFieldsRequest
): Promise<UpdateQuickBooksCustomerFieldsResult> {
  const base = resolveBase(apiBase);
  const response = await authFetch(`${base}/api/contacts/${contactId}/quickbooks/customer/update`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
  const data = await json<ContactApiResponse>(response);
  if (data.status === 'success') {
    return {
      qb_customer_id: data.data?.qb_customer_id || '',
      fields: Array.isArray(data.data?.fields) ? data.data.fields : [],
      display_name: data.data?.display_name ?? null,
    };
  }
  const message = typeof data?.message === 'string' ? data.message : 'Failed to update QuickBooks customer';
  throw new Error(message);
}

// =============================================================================
// Statement API Types and Functions
// =============================================================================

export interface StatementInvoice {
  invoice_id: string;
  doc_number: string;
  txn_date: string | null;
  due_date: string | null;
  total_amount: number;
  balance: number;
  invoice_link: string | null;
}

export interface StatementPreviewResponse {
  invoices: StatementInvoice[];
  count: number;
  total_balance: number;
  contact_email: string | null;
  contact_name: string;
  default_cc_emails: string[];
}

export interface SendStatementRequest {
  send_to?: string;
  greeting_name?: string;
  custom_message?: string;
  /** Per-send CC override; omit for Contact defaults or pass [] to suppress defaults */
  cc?: string | string[];
  allow_credit_card?: boolean;
  allow_ach?: boolean;
  include_combined_invoice_pdf?: boolean;
  /** If true, prepend "TEST: " to subject line */
  test_mode?: boolean;
}

export interface SendStatementResponse {
  success: boolean;
  invoice_count: number;
  total_balance: number;
  sent_to: string;
  sent_from: string;
}

const STATEMENT_SEND_TIMEOUT_MS = 115_000;

/**
 * Get statement preview data: open invoices for a contact's QuickBooks customer.
 */
export async function getContactStatementPreview(
  apiBase: string,
  contactId: string,
  options: { signal?: AbortSignal } = {}
): Promise<StatementPreviewResponse> {
  const base = resolveBase(apiBase);
  const url = `${base}/api/contacts/${contactId}/statement/preview`;
  
  const fetchPreview = async () => {
    const response = await authFetch(url, { signal: options.signal });
    const data = await json<ContactApiResponse>(response);
    if (data.status === 'success' && data.data) {
      return data.data as StatementPreviewResponse;
    }
    const message = typeof data?.message === 'string' ? data.message : 'Failed to load statement preview';
    throw new Error(message);
  };

  if (options.signal) {
    return fetchPreview();
  }

  return dedupeRequest(url, fetchPreview);
}

/**
 * Send a statement email with all open invoices for a contact.
 */
export async function sendContactStatement(
  apiBase: string,
  contactId: string,
  payload: SendStatementRequest = {}
): Promise<SendStatementResponse> {
  const base = resolveBase(apiBase);
  let responseReceived = false;

  try {
    const response = await authFetch(`${base}/api/contacts/${contactId}/statement/send`, {
      method: 'POST',
      body: JSON.stringify(payload),
      timeoutMs: STATEMENT_SEND_TIMEOUT_MS,
    });
    responseReceived = true;

    let data: ContactApiResponse;
    try {
      data = await json<ContactApiResponse>(response);
    } catch (error) {
      if (response.ok) {
        throw new StatementSendUnconfirmedError();
      }
      throw error;
    }
    if (data.status === 'success' && data.data) {
      return data.data as SendStatementResponse;
    }
    const message = typeof data?.message === 'string' ? data.message : 'Failed to send statement';
    throw new Error(message);
  } catch (error) {
    if (!responseReceived || isAbortError(error)) {
      throw new StatementSendUnconfirmedError();
    }
    throw error;
  }
}

// =============================================================================
// Review Request Email API
// =============================================================================

export type ReviewRequestServiceType =
  | 'solar_consultation'
  | 'removal_reinstallation'
  | 'battery_installation'
  | 'solar_installation'
  | 'solar_battery_installation'
  | 'service_work';

export interface ReviewRequestLastSent {
  sent_at: string;
  service_type: ReviewRequestServiceType;
  service_label: string;
  sent_to: string;
}

export interface ReviewRequestPreview {
  sender: string;
  recipient: string;
  service_type: ReviewRequestServiceType;
  service_label: string;
  subject: string;
  plain_text: string;
  last_sent: ReviewRequestLastSent | null;
  services: Array<{ value: ReviewRequestServiceType; label: string }>;
}

export interface ReviewRequestPayload {
  service_type: ReviewRequestServiceType;
  send_to?: string;
  personal_note?: string;
  test_mode?: boolean;
  confirm_resend?: boolean;
}

export interface ReviewRequestSendResult {
  success: boolean;
  sent_to: string;
  sent_from: string;
  service_type: ReviewRequestServiceType;
  service_label: string;
  test_mode: boolean;
  history_saved: boolean;
  tracking_saved: boolean;
}

type ReviewRequestApiResponse = ApiErrorPayload & {
  data?: ReviewRequestPreview | ReviewRequestSendResult;
};

export async function previewContactReviewRequest(
  apiBase: string,
  contactId: string,
  payload: ReviewRequestPayload,
  options: { signal?: AbortSignal } = {},
): Promise<ReviewRequestPreview> {
  const base = resolveBase(apiBase);
  const response = await authFetch(`${base}/api/contacts/${contactId}/review-request/preview`, {
    method: 'POST',
    signal: options.signal,
    body: JSON.stringify(payload),
  });
  const data = await json<ReviewRequestApiResponse>(response);
  if (data.status === 'success' && data.data) {
    return data.data as ReviewRequestPreview;
  }
  throw new Error(data.message || 'Failed to preview review request');
}

export async function sendContactReviewRequest(
  apiBase: string,
  contactId: string,
  payload: ReviewRequestPayload,
): Promise<ReviewRequestSendResult> {
  const base = resolveBase(apiBase);
  const response = await authFetch(`${base}/api/contacts/${contactId}/review-request/send`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
  const data = await json<ReviewRequestApiResponse>(response);
  if (data.status === 'success' && data.data) {
    return data.data as ReviewRequestSendResult;
  }
  throw new Error(data.message || 'Failed to send review request');
}

export async function updateContactReviewTracking(apiBase: string, contactId: string, payload: ReviewTrackingPatch): Promise<ContactRecord> {
  const response = await authFetch(`${resolveBase(apiBase)}/api/contacts/${contactId}/review-tracking`, {
    method: 'PATCH', body: JSON.stringify(payload),
  });
  const data = await json<ContactApiResponse>(response);
  if (data.status !== 'success') throw new Error(data.message || 'Failed to save review tracking');
  return normalizeContactRecord(data.data);
}

export interface EnphaseAccessRequestLastSent {
  sent_at: string;
  sent_to: string;
}

export interface EnphaseAccessRequestPreview {
  sender: string;
  recipient: string;
  subject: string;
  plain_text: string;
  html: string;
  last_sent: EnphaseAccessRequestLastSent | null;
}

export interface EnphaseAccessRequestPayload {
  send_to?: string;
  personal_note?: string;
  test_mode?: boolean;
  confirm_resend?: boolean;
}

export interface EnphaseAccessRequestSendResult {
  success: boolean;
  sent_to: string;
  sent_from: string;
  test_mode: boolean;
  history_saved: boolean;
}

type EnphaseAccessRequestApiResponse = ApiErrorPayload & {
  data?: EnphaseAccessRequestPreview | EnphaseAccessRequestSendResult;
};

export async function previewContactEnphaseAccessRequest(
  apiBase: string,
  contactId: string,
  payload: EnphaseAccessRequestPayload,
  options: { signal?: AbortSignal } = {},
): Promise<EnphaseAccessRequestPreview> {
  const base = resolveBase(apiBase);
  const response = await authFetch(`${base}/api/contacts/${contactId}/enphase-access-request/preview`, {
    method: 'POST',
    signal: options.signal,
    body: JSON.stringify(payload),
  });
  const data = await json<EnphaseAccessRequestApiResponse>(response);
  if (data.status === 'success' && data.data) {
    return data.data as EnphaseAccessRequestPreview;
  }
  throw new Error(data.message || 'Failed to preview Enphase access request');
}

export async function sendContactEnphaseAccessRequest(
  apiBase: string,
  contactId: string,
  payload: EnphaseAccessRequestPayload,
): Promise<EnphaseAccessRequestSendResult> {
  const base = resolveBase(apiBase);
  const response = await authFetch(`${base}/api/contacts/${contactId}/enphase-access-request/send`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
  const data = await json<EnphaseAccessRequestApiResponse>(response);
  if (data.status === 'success' && data.data) {
    return data.data as EnphaseAccessRequestSendResult;
  }
  throw new Error(data.message || 'Failed to send Enphase access request');
}
